<script lang="ts">
  /**
   * WorldCreationWizard — 3-mode world creation (freeform, quick pick, full wizard).
   */
  import "../../styles/wizard.css";
  import type { WorldCreationParams } from "$lib/api/invoke";
  import AsciiAnimation from "./AsciiAnimation.svelte";

  interface Props {
    onComplete: (params: WorldCreationParams) => void;
    onBack: () => void;
  }

  let { onComplete, onBack }: Props = $props();

  type WizardMode = "select" | "freeform" | "quick" | "full";
  type FullStep = "genre" | "era" | "tone" | "details" | "confirm";

  let mode = $state<WizardMode>("select");
  let fullStep = $state<FullStep>("genre");

  /* Shared state */
  let genre = $state("");
  let era = $state("");
  let tone = $state("");
  let freeformPrompt = $state("");
  let worldName = $state("");
  let additionalDetails = $state("");

  const GENRES = [
    "Dark Fantasy",
    "Cosmic Horror",
    "Grimdark Sci-Fi",
    "Sword & Sorcery",
    "Post-Apocalyptic",
    "Gothic Horror",
    "Mythic Bronze Age",
  ];

  const ERAS = ["Ancient", "Medieval", "Renaissance", "Victorian", "Modern", "Far Future"];

  const TONES = ["Bleak", "Darkly Comedic", "Mythic", "Surreal", "Gritty Realism"];

  let genreIndex = $state(0);
  let eraIndex = $state(0);
  let toneIndex = $state(0);

  /** Genre-themed name fragments for random world name generation. */
  const GENRE_NAMES: Record<string, string[]> = {
    "Dark Fantasy": [
      "The Sunken",
      "Ashenmoor",
      "Grimhollow",
      "The Blighted",
      "Duskfall",
      "Wraithfen",
      "Thornkeep",
      "The Hollow",
      "Blacktide",
      "Moonscar",
    ],
    "Cosmic Horror": [
      "The Void",
      "R'lyeh's Shadow",
      "The Spiral",
      "Abyssal",
      "The Crawling",
      "Null Point",
      "The Breach",
      "Echo Depths",
      "The Maw",
      "Cyclopean",
    ],
    "Grimdark Sci-Fi": [
      "Sector 7-G",
      "The Crucible",
      "Ash Station",
      "The Drift",
      "Forge World",
      "Iron Cradle",
      "The Blight",
      "Terminus",
      "Null Grid",
      "The Furnace",
    ],
    "Post-Apocalyptic": [
      "The Wastes",
      "Last Haven",
      "Scorchfield",
      "The Rust",
      "Deadzone",
      "Ashen Plains",
      "The Crater",
      "Boneyard",
      "Dustfall",
      "The Scar",
    ],
    "Gothic Horror": [
      "Ravenmoor",
      "The Manor",
      "Blightwick",
      "Gallowsgate",
      "The Pale",
      "Nighthollow",
      "Crowsreach",
      "The Crypt",
      "Ashford",
      "Mistwall",
    ],
  };

  const DEFAULT_NAMES = [
    "The Depths",
    "Shadowmere",
    "Ironhold",
    "The Crossing",
    "Stormbreak",
    "Darkwater",
    "Thornwood",
    "The Abyss",
    "Bleakstone",
    "Hellgate",
  ];

  /** Pick a random world name based on the currently selected genre. */
  function randomWorldName(): void {
    const names = GENRE_NAMES[genre] ?? DEFAULT_NAMES;
    worldName = names[Math.floor(Math.random() * names.length)];
  }

  const assembledPrompt = $derived(() => {
    const parts: string[] = [];
    if (genre) parts.push(`Genre: ${genre}`);
    if (era) parts.push(`Era: ${era}`);
    if (tone) parts.push(`Tone: ${tone}`);
    if (freeformPrompt) parts.push(freeformPrompt);
    if (additionalDetails) parts.push(additionalDetails);
    return parts.join(". ");
  });

  function selectGenre(g: string): void {
    genre = g;
    if (!worldName) randomWorldName();
    if (mode === "quick") {
      fullStep = "confirm";
    } else {
      fullStep = "era";
    }
  }

  function selectEra(e: string): void {
    era = e;
    fullStep = "tone";
  }

  function selectTone(t: string): void {
    tone = t;
    fullStep = "details";
  }

  function handleConfirm(): void {
    const name = worldName.trim() || "Untitled World";
    const seed = crypto.randomUUID().replace(/-/g, "").slice(0, 16);
    const prompt = assembledPrompt();
    onComplete({
      name,
      seed,
      settingPrompt: prompt,
      genre: genre || "custom",
      era: era || "unspecified",
      tone: tone || "bleak",
    });
  }

  function handleBack(): void {
    if (mode === "select") {
      onBack();
    } else if (mode === "freeform") {
      mode = "select";
    } else if (mode === "quick") {
      if (fullStep === "confirm") {
        genre = "";
        fullStep = "genre";
      } else {
        mode = "select";
      }
    } else {
      // full wizard — step back
      if (fullStep === "genre") mode = "select";
      else if (fullStep === "era") fullStep = "genre";
      else if (fullStep === "tone") fullStep = "era";
      else if (fullStep === "details") fullStep = "tone";
      else if (fullStep === "confirm") fullStep = "details";
    }
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === "Escape") {
      e.preventDefault();
      handleBack();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="wcw-root">
  <AsciiAnimation animationLevel="subtle" glyphPercent={10} glyphSpeedMs={300} />
  <div class="wizard">
    {#if mode === "select"}
      <p class="wizard__heading">Create Your World</p>
      <p class="wizard__text">Choose how you want to build your world:</p>

      <div class="wcw-modes">
        <button
          class="wcw-mode-card"
          onclick={() => {
            mode = "freeform";
          }}
        >
          <span class="wcw-mode-card__title">Describe Your World</span>
          <span class="wcw-mode-card__desc">Write a freeform description</span>
        </button>
        <button
          class="wcw-mode-card"
          onclick={() => {
            mode = "quick";
            fullStep = "genre";
          }}
        >
          <span class="wcw-mode-card__title">Quick Pick</span>
          <span class="wcw-mode-card__desc">Pick a genre, name it, go</span>
        </button>
        <button
          class="wcw-mode-card"
          onclick={() => {
            mode = "full";
            fullStep = "genre";
          }}
        >
          <span class="wcw-mode-card__title">Full Wizard</span>
          <span class="wcw-mode-card__desc">Genre + era + tone + details</span>
        </button>
      </div>

      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={onBack}>Back to Menu</button>
      </div>
    {:else if mode === "freeform"}
      <p class="wizard__heading">Describe Your World</p>
      <p class="wizard__text">
        Write anything — a setting, a mood, an idea. The AI will build from it.
      </p>
      <label class="wizard__label">
        World Description:
        <textarea
          class="wizard__textarea"
          rows={4}
          maxlength={512}
          placeholder="A sunken cathedral beneath a poisoned sea, where drowned saints still whisper..."
          bind:value={freeformPrompt}
        ></textarea>
      </label>
      <label class="wizard__label">
        World Name:
        <div class="wcw-name-row">
          <input
            class="wizard__input"
            type="text"
            placeholder="The Drowned Cathedral"
            bind:value={worldName}
          />
          <button class="wcw-reroll" onclick={randomWorldName}>[Reroll]</button>
        </div>
      </label>
      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
        <button class="wizard__btn" disabled={!freeformPrompt.trim()} onclick={handleConfirm}
          >Create World</button
        >
      </div>
    {:else if (mode === "quick" || mode === "full") && fullStep === "genre"}
      <p class="wizard__heading">Choose a Genre</p>
      <div class="wcw-grid">
        {#each GENRES as g, i}
          <button
            class="wcw-card"
            class:wcw-card--selected={genreIndex === i}
            onclick={() => {
              genreIndex = i;
              selectGenre(g);
            }}
            onmouseenter={() => {
              genreIndex = i;
            }}
          >
            {g}
          </button>
        {/each}
      </div>
      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
      </div>
    {:else if mode === "full" && fullStep === "era"}
      <p class="wizard__heading">Choose an Era</p>
      <p class="wizard__text wizard__text--dim">Genre: {genre}</p>
      <div class="wcw-grid">
        {#each ERAS as e, i}
          <button
            class="wcw-card"
            class:wcw-card--selected={eraIndex === i}
            onclick={() => {
              eraIndex = i;
              selectEra(e);
            }}
            onmouseenter={() => {
              eraIndex = i;
            }}
          >
            {e}
          </button>
        {/each}
      </div>
      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
      </div>
    {:else if mode === "full" && fullStep === "tone"}
      <p class="wizard__heading">Choose a Tone</p>
      <p class="wizard__text wizard__text--dim">Genre: {genre} | Era: {era}</p>
      <div class="wcw-grid">
        {#each TONES as t, i}
          <button
            class="wcw-card"
            class:wcw-card--selected={toneIndex === i}
            onclick={() => {
              toneIndex = i;
              selectTone(t);
            }}
            onmouseenter={() => {
              toneIndex = i;
            }}
          >
            {t}
          </button>
        {/each}
      </div>
      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
      </div>
    {:else if mode === "full" && fullStep === "details"}
      <p class="wizard__heading">Additional Details</p>
      <p class="wizard__text wizard__text--dim">Genre: {genre} | Era: {era} | Tone: {tone}</p>
      <label class="wizard__label">
        Any extra details about your world:
        <textarea
          class="wizard__textarea"
          rows={3}
          maxlength={256}
          placeholder="Optional: cursed lands, warring factions, ancient ruins..."
          bind:value={additionalDetails}
        ></textarea>
      </label>
      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
        <button
          class="wizard__btn"
          onclick={() => {
            fullStep = "confirm";
          }}>Next</button
        >
      </div>
    {:else if fullStep === "confirm"}
      <p class="wizard__heading">Name Your World</p>
      <p class="wizard__text wizard__text--dim">
        {genre}{era ? ` | ${era}` : ""}{tone ? ` | ${tone}` : ""}
      </p>
      <label class="wizard__label">
        World Name:
        <div class="wcw-name-row">
          <input
            class="wizard__input"
            type="text"
            placeholder="The Shattered Crypt"
            bind:value={worldName}
          />
          <button class="wcw-reroll" onclick={randomWorldName}>[Reroll]</button>
        </div>
      </label>
      {#if additionalDetails || freeformPrompt}
        <p class="wizard__text wizard__text--dim" style="font-size: 0.8rem;">
          Setting: {assembledPrompt()}
        </p>
      {/if}
      <div class="wizard__actions">
        <button class="wizard__btn wizard__btn--dim" onclick={handleBack}>Back</button>
        <button class="wizard__btn" onclick={handleConfirm}>Create World</button>
      </div>
    {/if}
  </div>
</div>

<style>
  .wcw-root {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
  }

  /* Center content within the wizard container for selection screens */
  .wcw-root :global(.wizard) {
    align-items: center;
    text-align: center;
  }

  .wcw-root :global(.wizard__label) {
    text-align: left;
  }

  .wcw-root :global(.wizard__actions) {
    justify-content: center;
  }

  .wcw-modes {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 32ch;
    margin: 0.5rem 0;
  }

  .wcw-mode-card {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.5rem 1rem;
    background: none;
    border: 1px solid var(--fg-dim);
    color: var(--fg);
    font-family: var(--font-mono);
    cursor: pointer;
    text-align: left;
  }

  .wcw-mode-card:hover,
  .wcw-mode-card:focus {
    border-color: var(--fg-bright);
    color: var(--fg-bright);
  }

  .wcw-mode-card__title {
    font-weight: bold;
    font-size: 0.95rem;
  }

  .wcw-mode-card__desc {
    color: var(--fg-dim);
    font-size: 0.8rem;
  }

  .wcw-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    width: 36ch;
    margin: 0.5rem 0;
  }

  .wcw-card {
    padding: 0.5rem;
    background: none;
    border: 1px solid var(--fg-dim);
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 0.85rem;
    cursor: pointer;
    text-align: center;
  }

  .wcw-card:hover,
  .wcw-card:focus,
  .wcw-card--selected {
    border-color: var(--fg-bright);
    color: var(--fg-bright);
  }

  .wcw-name-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .wcw-name-row .wizard__input {
    flex: 1;
  }

  .wcw-reroll {
    background: none;
    border: 1px solid var(--fg);
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 0.8rem;
    padding: 0.35rem 0.5rem;
    cursor: pointer;
    white-space: nowrap;
  }

  .wcw-reroll:hover,
  .wcw-reroll:focus {
    border-color: var(--fg-bright);
    color: var(--fg-bright);
  }
</style>
